import { useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useCanAccess, useGetIdentity, useGetOne, useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Sale } from "../types";
import { categoryKey, countryName, phaseKey } from "../projects/projectUtils";
import { QUERY_KEY } from "../projects/DailyTeasers";
import { useCoverage, useZones } from "../projects/territories";
import { endingFor, formatStoryValue } from "./onboardingLogic";
import type { SceneModel, StoryValue } from "./types";

export type SceneProps = {
  model: SceneModel;
  active: boolean;
  reducedMotion: boolean;
  onClose: () => void;
  t: (key: string, options?: object) => string;
  tr: (fullKey: string, options?: object) => string;
  locale: string;
};

const fmt = (n: number, locale: string) => n.toLocaleString(locale === "fr" ? "fr-FR" : "en-GB");

/** Compteur qui monte jusqu'à sa valeur en 1,2 s ; directement à sa valeur en mouvement réduit. */
const Count = ({ value, still, locale }: { value: number; still: boolean; locale: string }) => {
  const [shown, setShown] = useState(still ? value : 0);
  useEffect(() => {
    if (still) return setShown(value);
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const p = Math.min((now - start) / 1200, 1);
      setShown(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, still]);
  return <span className="onb-num">{fmt(shown, locale)}</span>;
};

const Example = ({ show, t }: { show: boolean; t: SceneProps["t"] }) => {
  return show ? <span className="onb-example">{t("example")}</span> : null;
};

const Radar = () => (
  <div className="onb-radar" aria-hidden>
    <div className="onb-sweep" />
    {[
      [22, 34, 0], [58, 22, 0.6], [44, 64, 1.2], [74, 52, 1.8], [30, 78, 2.4], [66, 80, 3],
    ].map(([x, y, delay]) => (
      <i key={`${x}-${y}`} className="onb-blip" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${delay}s` }} />
    ))}
  </div>
);

const SceneWorld = ({ model, reducedMotion, t, locale }: SceneProps) => {
  const c = model.counts;
  return (
    <section className="onb-scene onb-split">
      <div>
        <div className="onb-kicker">{t("scene.1.kicker")}</div>
        <h2 className="onb-title">{t("scene.1.title")}</h2>
        {c ? (
          <dl className="onb-counts">
            <div><dt>{t("scene.1.projects")}</dt><dd><Count value={c.projects} still={reducedMotion} locale={locale} /></dd></div>
            <div><dt>{t("scene.1.sources")}</dt><dd><Count value={c.sources} still={reducedMotion} locale={locale} /></dd></div>
            <div><dt>{t("scene.1.countries")}</dt><dd><Count value={c.countries} still={reducedMotion} locale={locale} /></dd></div>
          </dl>
        ) : (
          <p className="onb-lead">{t("scene.1.lead")}</p>
        )}
      </div>
      <Radar />
    </section>
  );
};

const Stamp = ({ method, t }: { method: StoryValue["method"]; t: SceneProps["t"] }) => {
  return (
    <span className={method === "extracted" ? "onb-stamp onb-measured" : "onb-stamp onb-inferred"}>
      {t(method === "extracted" ? "stamp.extracted" : "stamp.inferred")}
    </span>
  );
};

const SceneEvidence = ({ model, t, tr, locale }: SceneProps) => {
  const c = model.counts;
  return (
    <section className="onb-scene onb-split">
      <div>
        <div className="onb-kicker">{t("scene.2.kicker")}</div>
        <h2 className="onb-title">{t("scene.2.title")}</h2>
        <ul className="onb-tags">
          {["notice", "deliberation", "permit", "press"].map((kind) => (
            <li key={kind}>{t(`scene.2.sources.${kind}`)}</li>
          ))}
        </ul>
        {c && (
          <p className="onb-lead">
            {t("scene.2.lead", {
              extracted: fmt(c.extracted, locale),
              inferred: fmt(c.inferred, locale),
            })}
          </p>
        )}
      </div>
      <div>
        <ol className="onb-cards">
          {model.values.items.map((v, i) => (
            <li key={v.field} style={{ animationDelay: `${0.4 + i * 0.35}s` }}>
              <span className="onb-field">{t(`fields.${v.field}`, { _: v.field })}</span>
              <strong>
                {formatStoryValue(v, { locale, structureTypeFr: model.values.structureTypeFr, t, tr })}
              </strong>
              <Stamp method={v.method} t={t} />
            </li>
          ))}
        </ol>
        <Example show={model.values.example} t={t} />
      </div>
    </section>
  );
};

const SceneScore = ({ model, reducedMotion, t, tr, locale }: SceneProps) => {
  const { value, criteria, reasons, example } = model.score;
  return (
    <section className="onb-scene onb-split">
      <div>
        <div className="onb-kicker">{t("scene.3.kicker")}</div>
        <h2 className="onb-title">{t("scene.3.title")}</h2>
        <ul className="onb-criteria">
          {criteria.map((key, i) => (
            <li key={key} style={{ animationDelay: `${0.6 + i * 0.3}s` }}>
              {tr(`resources.projects.score.criteria.${key}`, { _: key })}
            </li>
          ))}
          {reasons.map((kind, i) => (
            <li key={`r-${kind}`} style={{ animationDelay: `${0.6 + (criteria.length + i) * 0.3}s` }}>
              {tr(`resources.projects.teaser.signals.${kind}`, { _: kind })}
            </li>
          ))}
        </ul>
        <Example show={example} t={t} />
      </div>
      <div
        className="onb-ring"
        style={{ "--onb-score": reducedMotion ? value : undefined, "--onb-target": value } as React.CSSProperties}
        role="img"
        aria-label={t("scene.3.ring", { score: value })}
      >
        <span><Count value={value} still={reducedMotion} locale={locale} /></span>
      </div>
    </section>
  );
};

const SceneDecision = ({ model, t }: SceneProps) => {
  const { items, blurred, example } = model.roles;
  return (
    <section className="onb-scene onb-split">
      <div>
        <div className="onb-kicker">{t("scene.4.kicker")}</div>
        <h2 className="onb-title">{t("scene.4.title")}</h2>
        <p className="onb-lead">{t(blurred ? "scene.4.lead_invited" : "scene.4.lead")}</p>
      </div>
      <div>
        <ol className="onb-chain">
          {items.map(({ group, company }, i) => (
            <li key={group} className={`onb-${group}`} style={{ animationDelay: `${0.4 + i * 0.35}s` }}>
              <span className="onb-field">{t(`scene.4.groups.${group}`)}</span>
              {blurred ? (
                <span className="onb-locked">
                  <Lock className="size-4" aria-hidden />
                  {/* Texte de remplissage flouté (traductions) : jamais le vrai nom, qui ne sort pas de la base pour l'invité. */}
                  <span className="onb-blur" aria-hidden="true">{t(`scene.4.filler.${group}`)}</span>
                  <span className="sr-only">{t("scene.4.hidden")}</span>
                </span>
              ) : (
                <strong>{company ?? t("scene.4.unknown")}</strong>
              )}
            </li>
          ))}
        </ol>
        <Example show={example} t={t} />
      </div>
    </section>
  );
};

const SceneDaily = ({ model, t, tr, locale }: SceneProps) => {
  return (
    <section className="onb-scene onb-split">
      <div>
        <div className="onb-kicker">{t("scene.5.kicker")}</div>
        <h2 className="onb-title">{t("scene.5.title")}</h2>
        <p className="onb-lead">{t("scene.5.lead")}</p>
      </div>
      <div>
        <ol className="onb-daily">
          {model.daily.items.map((row, i) => (
            <li key={i} style={{ animationDelay: `${0.4 + i * 0.3}s` }}>
              <span className="onb-daily-text">
                <span>
                  {row.name ?? (row.category ? tr(categoryKey(row.category)) : t("scene.5.project"))}
                  {row.country && <> · {countryName(row.country, locale)}</>}
                </span>
                {(row.phase || row.amount_band) && (
                  <small>
                    {[
                      row.phase && tr(phaseKey(row.phase), { _: row.phase }),
                      row.amount_band && tr(`resources.projects.teaser.bands.${row.amount_band}`, { _: row.amount_band }),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                )}
              </span>
              <b>{row.score ?? t("scene.5.no_score")}</b>
            </li>
          ))}
        </ol>
        <Example show={model.daily.example} t={t} />
      </div>
    </section>
  );
};

const SceneYours = ({ model, onClose, t, tr, locale }: SceneProps) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const invited = model.account === "invited";
  // Offre cachée hors gestionnaire (règle `offer` de canAccess) : l'invité finit comme les autres, sur ses 10 du jour.
  const offerOpen = useCanAccess({ resource: "offer", action: "read" }).canAccess === true;
  const toOffer = invited && offerOpen;
  const ending = endingFor(toOffer ? "invited" : "team");
  const { data: zones = [] } = useZones();
  const { data: coverage = [] } = useCoverage();
  // Zone déjà enregistrée, lue comme sur la page Accueil (« Votre zone d'intérêt »).
  const { identity } = useGetIdentity();
  const { data: sale } = useGetOne<Sale>(
    "sales",
    { id: identity?.id ?? 0 },
    { enabled: invited && identity?.id != null },
  );
  const saved = sale?.interest_zone
    ? `zone:${sale.interest_zone}`
    : sale?.interest_country
      ? `country:${sale.interest_country}`
      : "";
  const [picked, setPicked] = useState<string | null>(null);
  const area = picked ?? saved;
  const choose = async (value: string) => {
    setPicked(value);
    const [kind, code] = value.split(":");
    const { error } = await getSupabaseClient().rpc("set_interest_area", {
      zone: kind === "zone" ? code : null,
      country: kind === "country" ? code : null,
    });
    if (error) {
      setPicked(null);
      return notify(t("scene.6.error"), { type: "error" });
    }
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ["sales"] });
    notify(t("scene.6.saved"));
  };
  return (
    <section className="onb-scene">
      <div className="onb-kicker">{t("scene.6.kicker")}</div>
      <h2 className="onb-title">{t(invited ? "scene.6.title_invited" : "scene.6.title")}</h2>
      {invited && (
        <label className="onb-zone">
          <span>{t("scene.6.zone")}</span>
          <select value={area} onChange={(e) => e.target.value && choose(e.target.value)}>
            <option value="" disabled>{t("scene.6.zone_placeholder")}</option>
            <optgroup label={t("scene.6.zones")}>
              {zones.map((z) => (
                <option key={z.code} value={`zone:${z.code}`}>
                  {tr(`resources.projects.zones.${z.code}`)}
                </option>
              ))}
            </optgroup>
            <optgroup label={t("scene.6.countries")}>
              {[...coverage]
                .sort((a, b) => countryName(a.country, locale).localeCompare(countryName(b.country, locale), locale))
                .map((c) => (
                  <option key={c.country} value={`country:${c.country}`}>
                    {countryName(c.country, locale)}
                  </option>
                ))}
            </optgroup>
          </select>
        </label>
      )}
      <div className="onb-actions">
        <Link to={ending.primaryTo} className="onb-primary onb-button" onClick={onClose}>
          {t(toOffer ? "scene.6.offer" : "scene.6.daily")}
        </Link>
        <Link to={ending.secondaryTo} className="onb-button" onClick={onClose}>
          {t("scene.6.explore")}
        </Link>
      </div>
    </section>
  );
};

export const SCENES = [SceneWorld, SceneEvidence, SceneScore, SceneDecision, SceneDaily, SceneYours];
